/** @jsx jsx */
import { useEffect, useRef, useState } from 'react'
import { jsx, Box, Grid, Text } from 'theme-ui'
import { DefaultBlocks } from 'components/sanity/DefaultBlocks'
import { EventMultiImage } from 'components/media/EventMultiImage'
import { NoTitleAccordion } from 'components/modules/NoTitleAccordion'
import { Button } from 'components/buttons/Button'
import { Line } from 'components/Line'
import { Arrow } from 'components/global/icons/Arrow'
import { CheckRadio } from 'components/global/CheckRadio'
import { useRouter } from 'next/router'
import Meta from 'components/Meta'
import { toPlainText } from 'lib/sanity/toPlainText'

const WinterFeast = ({ data }) => {
  const { musicColumns = [], stallholders = [] } = data
  const [selectOpen, setSelectOpen] = useState(false)

  // Initial filter values
  const filters = ['dairyFree', 'glutenFree', 'vegan', 'vegetarian']
  const [filterBy, setFilterBy] = useState([])

  // Function to filter the stallholders
  const filteredStallholders = stallholders
    // The components are now mapped from the filtered results
    .map(stallholder => {
      // We cross check each selected filter with each stallholder
      // Here we are checking if filterName is not only included but also set to true stallholder data object
      const hidden = filterBy.some(filterName => !stallholder[filterName])
      return (
        <Stallholder data={stallholder} key={stallholder._key} hidden={hidden}>
          stallholder
        </Stallholder>
      )
    })

  const updateFilters = event => {
    const target = event.target
    const value = target.type === 'checkbox' ? target.checked : target.value
    const name = target.name

    // If the checkbox value is false remove the filter from filterBy
    // Otherwise add the filter to the filterBy
    if (value === false) {
      const filteredby = filterBy.filter(filterName => filterName != name)
      setFilterBy(filteredby)
    } else {
      setFilterBy([...filterBy, name])
    }
  }

  const filterToggles = filters.map(filterName => (
    <li
      sx={{
        display: 'flex',
      }}
      key={filterName}
    >
      <CheckRadio
        title={filterName}
        type="checkbox"
        color="black"
        onChange={updateFilters}
        checked={filterBy.includes(filterName)}
        id={`filter-${filterName}`}
      />
    </li>
  ))

  return (
    <>
      <Box mt={[6, 10]}>
        <Button
          aria-expanded={selectOpen}
          variant={'smallCopySmallCapsClearReverse'}
          onClick={() => {
            setSelectOpen(!selectOpen)
          }}
          sx={{
            pb: ['4px'],
          }}
        >
          <Arrow color="red" upsideDown={selectOpen} sx={{ mr: 1 }} />
          <Text
            as="span"
            sx={{
              ml: 1,
            }}
          >
            Filter {filterBy.length > 0 && `(${filterBy.length})`}
          </Text>
        </Button>
        <Line color="red" />
        <Grid
          columns={[2, 3, 4]}
          sx={{
            position: 'relative',
            pt: ['10px'],
            columnGap: [2, 4],
            rowGap: ['1.92em'],
          }}
        >
          <NoTitleAccordion
            id={'winter-feast-select'}
            isOpen={selectOpen}
            sx={{
              position: 'absolute',
              top: 0,
              zIndex: 500,
              backgroundColor: 'black',
              ':before': {
                content: "''",
                position: 'absolute',
                width: '4px',
                height: '100%',
                top: 0,
                left: '-4px',
                backgroundColor: 'black',
              },
              ':after': {
                content: "''",
                position: 'absolute',
                width: '4px',
                height: '100%',
                right: '-4px',
                top: 0,
                backgroundColor: 'black',
              },
            }}
          >
            <ul
              sx={{
                margin: 0,
                padding: 0,
                pt: '10px',
                pb: 1,
                listStyle: 'none',
              }}
            >
              {filterToggles}
            </ul>
          </NoTitleAccordion>
          {filteredStallholders}
        </Grid>
      </Box>
      <Box mt={[6, 10]}>
        <Text
          variant={`copyCapsReverse`}
          sx={{
            display: 'block',
            pt: 2,
            mt: [8],
            mb: '4px',
          }}
        >
          Music
        </Text>
        <Line color="red" />
        <Grid
          columns={[2, 3, 4]}
          sx={{
            columnGap: [2, 4],
            rowGap: ['1.92em'],
            mt: [2],
          }}
        >
          {musicColumns.map(col => (
            <Box key={col._key}>
              <DefaultBlocks blocks={col.copy} reverseColor={true} />
            </Box>
          ))}
        </Grid>
      </Box>
    </>
  )
}

const Stallholder = React.memo(({ data, hidden }) => {
  const router = useRouter()
  const [includeMeta, setIncludeMeta] = useState(
    router.query.slug[1] === data.slug.current,
  )
  const [isOpen, setIsOpen] = useState(
    router.query.slug[1] === data.slug.current,
  )
  const [instantAnimation, setInstantAnimation] = useState(
    router.query.slug[1] === data.slug.current,
  )

  const buttonRef = useRef()
  const handleClick = () => {
    setIsOpen(!isOpen)
    setIncludeMeta(false)
  }

  useEffect(() => {
    if (hidden) setIsOpen(false)
  }, [hidden])

  return (
    <Box
      sx={{
        display: hidden ? 'none' : 'block',
      }}
    >
      {includeMeta && (
        <Meta
          metaFields={{
            metaTitle: `Winter Feast | ${data.title}`,
            metaDescription: data.copy ? toPlainText(data.copy) : undefined,
            openImage: data.image || undefined,
          }}
        />
      )}
      <Button
        onClick={handleClick}
        variant="clear"
        sx={{
          display: 'block',
          width: '100%',
        }}
        ref={buttonRef}
      >
        <EventMultiImage imageAsset={data.image} />
        <Text
          variant="copyCapsReverse"
          sx={{
            display: 'block',
            pt: 2,
          }}
        >
          {data.title}
        </Text>
      </Button>
      <NoTitleAccordion
        id={data._key}
        isOpen={isOpen}
        instantAnimation={instantAnimation}
        setInstantAnimation={setInstantAnimation}
        offsetRef={buttonRef}
        scrollTo={true}
      >
        <Box
          pt={3}
          pb={'2px'}
          sx={{
            '> p:last-of-type': {
              mb: 0,
            },
          }}
        >
          {data.copy && (
            <DefaultBlocks blocks={data.copy} reverseColor={true} />
          )}
        </Box>
      </NoTitleAccordion>
      {/* Dietary Info */}
      <Box pt={1}>
        {data.dairyFree && (
          <Text
            variant="smallCopyReverse"
            sx={{
              display: 'block',
            }}
          >
            * Dairy Free
          </Text>
        )}
        {data.glutenFree && (
          <Text
            variant="smallCopyReverse"
            sx={{
              display: 'block',
            }}
          >
            * Gluten Free
          </Text>
        )}
        {data.vegan && (
          <Text
            variant="smallCopyReverse"
            sx={{
              display: 'block',
            }}
          >
            * Vegan
          </Text>
        )}
        {data.vegetarian && (
          <Text
            variant="smallCopyReverse"
            sx={{
              display: 'block',
            }}
          >
            * Vegetarian
          </Text>
        )}
      </Box>
    </Box>
  )
})

export { WinterFeast }
